import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { formatPhoneNumber } from '../utils/formatters';

interface Practice {
  uuid: string;
  name: string;
  description?: string;
  phone?: string;
  email?: string;
  website?: string;
  address?: string;
  city?: string;
  state?: string;
  slug?: string;
  specialties: string[];
  attributes?: {
    parking?: boolean;
    wheelchair_accessible?: boolean;
    emergency_services?: boolean;
    [key: string]: any;
  };
  accepts_new_patients: boolean;
}

const STATE_NAMES: { [key: string]: string } = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
  'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
  'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
  'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
  'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
  'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
  'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
  'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
};

const PracticesByCity: React.FC = () => {
  const { state, city } = useParams<{ state: string; city: string }>();
  const navigate = useNavigate();
  const [practices, setPractices] = useState<Practice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPractices = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/practices/state/${state}/city/${city}`
        );

        if (!response.ok) {
          if (response.status === 404) {
            setError('No practices found in this location');
          } else {
            setError('Failed to load practices');
          }
          return;
        }

        const data = await response.json();
        setPractices(data);
      } catch (err) {
        console.error('Error fetching practices:', err);
        setError('Failed to load practices');
      } finally {
        setLoading(false);
      }
    };

    if (state && city) {
      fetchPractices();
    }
  }, [state, city]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading practices...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Oops!</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate(`/practice/${state}`)}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Back to {STATE_NAMES[state?.toUpperCase() || '']} practices
          </button>
        </div>
      </div>
    );
  }

  const stateName = STATE_NAMES[state?.toUpperCase() || ''] || state;
  const cityName = city?.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      {/* Breadcrumb Schema for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://helppet.ai"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Practices",
              "item": "https://helppet.ai/practices"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": stateName,
              "item": `https://helppet.ai/practice/${state}`
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": `${cityName}, ${stateName}`,
              "item": `https://helppet.ai/practice/${state}/${city}`
            }
          ]
        })}
      </script>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="mb-6 flex items-center space-x-2 text-sm">
          <Link to="/practices" className="text-blue-600 hover:text-blue-700">
            All Practices
          </Link>
          <span className="text-gray-400">/</span>
          <Link to={`/practice/${state}`} className="text-blue-600 hover:text-blue-700">
            {stateName}
          </Link>
          <span className="text-gray-400">/</span>
          <span className="text-gray-600">{cityName}</span>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Veterinary Practices in {cityName}, {stateName}
          </h1>
          <p className="text-xl text-gray-600">
            {practices.length} {practices.length === 1 ? 'practice' : 'practices'} found
          </p>
        </div>

        {/* Practices Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {practices.map((practice) => (
            <div
              key={practice.uuid}
              className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-6"
            >
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {practice.name}
              </h3>
              
              {practice.description && (
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {practice.description}
                </p>
              )}

              {practice.specialties && practice.specialties.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {practice.specialties.slice(0, 3).map((specialty, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full"
                    >
                      {specialty}
                    </span>
                  ))}
                  {practice.specialties.length > 3 && (
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                      +{practice.specialties.length - 3} more
                    </span>
                  )}
                </div>
              )}

              <div className="space-y-2 text-sm text-gray-600 mb-4">
                {practice.address && (
                  <p className="truncate">{practice.address}</p>
                )}
                {practice.phone && (
                  <p>{formatPhoneNumber(practice.phone)}</p>
                )}
              </div>

              {/* Key Attributes */}
              {practice.attributes && (
                <div className="flex flex-wrap gap-2 mb-4 text-xs">
                  {practice.attributes.parking && (
                    <span className="text-gray-600">🅿️ Parking</span>
                  )}
                  {practice.attributes.emergency_services && (
                    <span className="text-gray-600">🚑 Emergency</span>
                  )}
                  {practice.attributes.wheelchair_accessible && (
                    <span className="text-gray-600">♿ Accessible</span>
                  )}
                </div>
              )}

              {practice.accepts_new_patients && (
                <div className="mb-4">
                  <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                    ✓ Accepting New Patients
                  </span>
                </div>
              )}

              <Link
                to={
                  practice.slug && practice.city && practice.state
                    ? `/practice/${practice.state}/${practice.city}/${practice.slug}`
                    : `/practices/${practice.uuid}`
                }
                className="block w-full text-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                View Details
              </Link>
            </div>
          ))}
        </div>

        {practices.length === 0 && !error && (
          <div className="text-center py-12">
            <p className="text-gray-600 mb-4">No practices found in {cityName}, {stateName}</p>
            <Link
              to={`/practice/${state}`}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              View all practices in {stateName} →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default PracticesByCity;

